import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/features/notifications';
import { UserSchema } from '@/features/users';

import { useCreateOfferMutation } from '../api';
import { CreateOfferFormFields } from '../components';

export type useOnCreateOfferProps = {
  id: string;
  onClose: () => void;
  user: UserSchema | undefined;
};

export function useOnCreateOffer({ id, onClose, user }: useOnCreateOfferProps) {
  const { t } = useTranslation(['offers']);
  const [createOffer, { isLoading }] = useCreateOfferMutation();
  const { add } = useNotifications();

  return useCallback(
    (data: CreateOfferFormFields) => {
      if (!id) return;
      if (!user) return;
      if (isLoading) return;
      if (!data.buyer_type) return;
      return createOffer({
        ...data,
        company: user.company.id,
        opportunity: id,
        buyer_type: data.buyer_type,
      })
        .unwrap()
        .then((createdOffer) => {
          add({
            id: new Date().toString(),
            title: t('Offer created'),
            description: t('Your new offer has been created'),
          });
          onClose();
          return createdOffer;
        })
        .catch((error) => {
          add({
            id: new Date().toString(),
            title: t('ERROR'),
            description: t('There was an error creating the offer. Please try again later.'),
          });
          onClose();
          throw error;
        });
    },
    [id, user, isLoading, t, add, createOffer, onClose]
  );
}
